import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserProfile } from '../../ui/business/BusinessProfile/parts/UserProfile/UserProfile';
import { UserSkeleton } from '../../ui/business/BusinessProfile/parts/UserSkeleton';
import { DashboardPage } from '../../ui/dashboard/DashboardPage';
import { IconKebab } from '../../ui/icon/IconKebab';
import { IconButton } from '../../ui/input/IconButton';
import { Box } from '../../ui/layout/Box';
import { DataTestId } from '../../ui/layout/DataTestIdWrapper';
import { Flex } from '../../ui/layout/Flex';
import { Menu, MenuItem } from '../../ui/navigation/Menu';
import { Typography } from '../../ui/typography/Typography';
import { UserCard, UserCardSkeleton } from '../../ui/users/UserCard';
import { canNotRegisterBusiness, isBlogger } from '../../utils/accessService';
import { useIsMobileSync } from '../../utils/getIsMobile';
import { getRegistrationLink } from '../../utils/getRegistrationLink';
import { ymTrackGoToRegistration } from '../../utils/metrika';
import { useBackButton } from '../../utils/useBackButton';
import { useMeta } from '../../utils/useMeta';
import { useTranslation } from '../../utils/useTranslation';
import { DialogRoute, useOpenDialog } from '../ModalRoute';
import { DIALOGS } from '../constants';
import { withAuthProtection } from '../withAuthProtection';
import { CpaContainer } from './CpaContainer/CpaContainer';
import { UserCollections } from './UserCollections';
import { UserCollectionsTabs } from './UserCollectionsTabs';
import { UserSubscriptions } from './UserSubscriptions';
import { useCollectionsTabs } from './hooks/useCollectionsTabs';
import { useCurrentUser } from './useCurrentUser';

const USER_AGREEMENT_LINK = 'https://yandex.ru/legal/ritm_termsofuse/';

export const UserDetails = withAuthProtection(() => {
    const openDialog = useOpenDialog();
    const currentUser = useCurrentUser();
    const onNavigateBack = useBackButton();
    const t = useTranslation();
    const isMobile = useIsMobileSync();
    const navigate = useNavigate();

    // личную страницу блогера редиректим на страницу бизнеса
    useEffect(() => {
        if (isBlogger(currentUser?.role) && currentUser?.profile?.publicId) {
            navigate(`/profile/${currentUser.profile.publicId}`);
        }
    }, [currentUser?.profile?.publicId, currentUser?.role, navigate]);

    useMeta(
        currentUser
            ? { title: `${currentUser.displayName ?? currentUser.login} • Яндекс Ритм` }
            : undefined,
    );

    const onSupportClick = useCallback(() => {
        window.open(
            `${process.env.REACT_APP_BETA_SUPPORT_FORM_REGULAR_LINK}?login=${currentUser?.login}`,
            '_blank',
        );
    }, [currentUser?.login]);

    const onMoveToRegistration = useCallback(() => {
        ymTrackGoToRegistration({
            page: 'Профиль пользователя',
            userLogin: currentUser?.login,
        });
        window.open(getRegistrationLink());
    }, [currentUser?.login]);

    const getContextMenu = useCallback(
        (params?: { appMenuItem: React.ReactElement }) => (
            <Menu
                trigger={
                    <IconButton variant="filled" dataTestId={DataTestId.PROFILE_KEBAB}>
                        <IconKebab />
                    </IconButton>
                }
                dataTestId={DataTestId.PROFILE_MENU}
            >
                <MenuItem
                    onClick={() => window.open(USER_AGREEMENT_LINK, '_blank')}
                    dataTestId={DataTestId.USER_AGREEMENT}
                >
                    <Typography>{t('Пользовательское соглашение')}</Typography>
                </MenuItem>
                <MenuItem onClick={onSupportClick} dataTestId={DataTestId.SUPPORT}>
                    <Typography>{t('Поддержка')}</Typography>
                </MenuItem>
                {!currentUser ||
                canNotRegisterBusiness(currentUser.businessRegistrationStatus) ? undefined : (
                    <MenuItem
                        onClick={onMoveToRegistration}
                        dataTestId={DataTestId.APPLY_BETA_TEST}
                    >
                        <Typography>{t('Начать публиковать контент')}</Typography>
                    </MenuItem>
                )}
                {params?.appMenuItem}
            </Menu>
        ),
        [currentUser, onMoveToRegistration, onSupportClick, t],
    );

    const isWideProfile = isMobile === false;

    const { hasTabs, collectionType, onChangeTab } = useCollectionsTabs();

    const profile = useMemo(() => {
        if (isMobile === null) {
            return null;
        }

        if (isWideProfile) {
            return currentUser ? (
                <UserProfile
                    mt={2}
                    mb={1.5}
                    user={currentUser}
                    onShowSubscriptions={() => openDialog(DIALOGS.SUBSCRIPTIONS)}
                    cpaButton={<CpaContainer />}
                >
                    {hasTabs ? (
                        <Flex width="100%" alignItems="center" mb={-2}>
                            <UserCollectionsTabs
                                mb={0}
                                mx={0}
                                overflow="hidden"
                                borderBottom="none"
                                collectionType={collectionType}
                                onChange={onChangeTab}
                            />
                        </Flex>
                    ) : null}
                </UserProfile>
            ) : (
                <UserSkeleton mt={2} mb={1.5} />
            );
        }

        return currentUser ? (
            <UserCard
                user={currentUser}
                onShowSubscriptions={() => openDialog(DIALOGS.SUBSCRIPTIONS)}
                cpaButton={<CpaContainer />}
            />
        ) : (
            <UserCardSkeleton />
        );
    }, [currentUser, isMobile, isWideProfile, openDialog, hasTabs, collectionType, onChangeTab]);

    return (
        <DashboardPage
            py={0}
            menuSlot={isMobile ? getContextMenu : undefined}
            centered={!isWideProfile}
            fullWidth={isWideProfile}
        >
            <Box
                width="100%"
                minHeight="100%"
                position="relative"
                background={{ xs: 'var(--background-primary)', sm: 'none' }}
            >
                {isWideProfile ? null : (
                    <Box
                        display={{ xs: 'none', md: 'block' }}
                        position="absolute"
                        top={0}
                        right={0}
                        m={1}
                        zIndex={5}
                    >
                        {getContextMenu()}
                    </Box>
                )}
                <Box mb={{ xs: 1, sm: 2 }}>{profile}</Box>
                <UserCollections
                    hasTabs={hasTabs ? !isWideProfile : false}
                    collectionType={collectionType}
                    columnsCount={isWideProfile ? 5 : 3}
                    onChangeTab={onChangeTab}
                    mr={isWideProfile ? 2 : 0}
                />
                <DialogRoute
                    name={DIALOGS.SUBSCRIPTIONS}
                    element={<UserSubscriptions onClose={onNavigateBack} />}
                />
            </Box>
        </DashboardPage>
    );
});
